"use client";

import React from "react";
import { Streamdown } from "streamdown";
import { ChevronDown } from "lucide-react";
import { useWatch } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Paragraph } from "@/components/ui/typography";
import Link from "@/components/link";

import CourseSection from "./course-section";

import useCollapsible from "@/hooks/use-collapsible";

import useMoengageEventTracker from "@/hooks/use-moengage";
import EVENT_NAME from "@/hooks/use-moengage/event-name";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute";

import { cn } from "@/lib/utils";

import Download from "./vectors/download.svg";

function CourseOverview({
  courseDescription,
  courseDescriptionHtml,
  brachureUrl,
}) {
  const trackMoengageEvent = useMoengageEventTracker();

  const { ref, isCollapsible } = useCollapsible(384);
  const courseMode = useWatch({ name: "mode" });

  const [isExpanded, setIsExpanded] = React.useState(false);

  const handleOnDownloadBrochureClick = () => {
    trackMoengageEvent(EVENT_NAME.BUTTON_CLICK, {
      [EVENT_ATTRIBUTE.BUTTON_NAME]: "download_brachure",
      [EVENT_ATTRIBUTE.COURSE_ONLINE]: courseMode === "online" ? "yes" : "no",
      [EVENT_ATTRIBUTE.SOURCE_SCREEN]: "pdp",
    });
  };

  return (
    <CourseSection
      id="course-overview"
      title="Course Overview"
      action={
        brachureUrl && (
          <Button
            size="small"
            className="bg-primary-bg border-primary-bg-strong text-primary hover:bg-primary-bg"
            block
            asChild
            onClick={handleOnDownloadBrochureClick}
          >
            <Link href={brachureUrl} target="_blank" rel="noreferrer">
              <Download />
              <Paragraph variant="b4" weight="sb" className="text-inherit">
                Download Brochure
              </Paragraph>
            </Link>
          </Button>
        )
      }
      cardHeaderClassName="flex [&_.card-action]:w-full gap-y-4 flex-col xs:grid xs:[&_.card-action]:w-auto"
      cardContentClassName="flex flex-col gap-y-4"
    >
      <div
        ref={ref}
        className={cn(
          "relative flex flex-col gap-y-2.5 text-grayscale-label",
          "[&_strong]:text-grayscale-header",
          "after:absolute after:bottom-0 after:left-0 after:w-full after:h-12",
          "after:bg-gradient-to-b after:from-transparent after:to-white after:pointer-events-none",
          !isExpanded && "max-h-62 overflow-hidden",
          !isCollapsible && "after:invisible",
          isExpanded && isCollapsible && "after:invisible"
        )}
      >
        {courseDescription && <Streamdown>{courseDescription}</Streamdown>}
        {courseDescriptionHtml && (
          <div
            className="space-y-2"
            dangerouslySetInnerHTML={{ __html: courseDescriptionHtml }}
          />
        )}
      </div>

      {isCollapsible && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="small"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            <Paragraph variant="b4" weight="sb" className="text-inherit">
              Read {isExpanded ? "less" : "more"}
            </Paragraph>

            <ChevronDown
              className={cn(
                "rotate-0 transition-transform duration-300",
                isExpanded && "-rotate-180"
              )}
            />
          </Button>
        </div>
      )}
    </CourseSection>
  );
}

export default CourseOverview;
